import { MESSAGE_TYPE, READ_STATUS } from 'constants/message';
import {
  COURSE_ASK_TAB,
  COURSE_ASK_TAB_PARAM,
  TCourseAskTab,
} from 'constants/course';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'routes/routeConfig';
import axios from 'plugins/api/axios';
import { useNotificationService } from 'services/NotificationService';

const { teachers } = axios;

/**
 * 點了就直接導到排課請求對應分頁的通知類型。
 *
 * 這類通知沒有詳情可看，內容就是「有新的單可以搶」，而搶課有倒數 ——
 * 多讓老師點一層會白白耗掉時間。其餘類型維持各自原本的行為。
 */
const COURSE_ASK_TAB_BY_MESSAGE_TYPE: Partial<
  Record<MESSAGE_TYPE, TCourseAskTab>
> = {
  [MESSAGE_TYPE.claimCourse]: COURSE_ASK_TAB.SINGLE_CLAIM,
  [MESSAGE_TYPE.claimBatchCourse]: COURSE_ASK_TAB.BATCH_CLAIM,
};

/**
 * 搶課通知的共用行為：判斷該導去哪個分頁、標已讀、導頁。
 *
 * 通知頁與右上鈴鐺彈窗都要做同一件事，抽出來才不會改一邊忘一邊。
 */
export const useClaimNotification = () => {
  const navigate = useNavigate();
  const { notifications, setNotifications } = useNotificationService();

  /** 不是搶課通知就回 undefined，呼叫端據此決定要不要接管點擊 */
  const getCourseAskTab = useCallback(
    (messageType: MESSAGE_TYPE) => COURSE_ASK_TAB_BY_MESSAGE_TYPE[messageType],
    [],
  );

  /**
   * 標記已讀。已讀的直接跳過，不重複打 API。
   *
   * ⚠️ 這段是從通知頁原樣搬過來的，刻意不改行為，但它其實靠兩個巧合成立：
   * context 給的 `notifications` 是 Provider 裡**加工後**的那一份（經過深拷貝），
   * 而 `setNotifications` 寫的是**原始**那一份。先就地改物件、再把加工後的陣列
   * 整個寫回原始 state，兩件事同時做才剛好讓畫面與 state 都對。
   *
   * 所以：(1) 列表元件不能加 `React.memo`，物件 reference 沒變會不重繪；
   * (2) 每呼叫一次就會讓 Provider 重跑一輪加工。要根治得改 Provider，
   * 讓已讀變成它自己的 action，不是呼叫端把衍生資料寫回原始 state。
   */
  const markAsRead = useCallback(
    async (id: string) => {
      const index = notifications.findIndex(item => item.id === id);
      if (index === -1) return;
      if (notifications[index].readStatus !== READ_STATUS.UNREAD) return;

      notifications[index].readStatus = READ_STATUS.READ;
      setNotifications(() => [...notifications]);
      await teachers.readMessage([id]);
    },
    [notifications, setNotifications],
  );

  const goToCourseAskTab = useCallback(
    (tab: TCourseAskTab) =>
      navigate(`${PATH.COURSE_ASK}?${COURSE_ASK_TAB_PARAM}=${tab}`),
    [navigate],
  );

  return { getCourseAskTab, markAsRead, goToCourseAskTab };
};
